// Port Wingo UI (React 19 + Tailwind 4) → Tailwind 3.4, preluat din portul ERP-ului (React 18). Vezi components/wingo/README.md.
import { useEffect, useState } from "react";
import { useMotionValue, type MotionValue } from "motion/react";

// Wingo UI Pro · https://wingo-ui.com/legal/license

export type UseKeyboardInsetOptions = {
  // stop measuring (and report 0)
  disabled?: boolean;
  // px of covered viewport below which it is not a keyboard (browser bars, the ios form accessory alone). default 80
  threshold?: number;
  // also write the inset in px to this css variable on <html>, e.g. "--keyboard-inset". default: none
  cssVar?: string;
};

export type KeyboardInset = {
  // px of the layout viewport the on-screen keyboard covers at the bottom, updated per frame without re-rendering.
  // lift a bar pinned to the bottom with style={{ y: useTransform(inset, (v) => -v) }}
  inset: MotionValue<number>;
  // re-renders only when it flips: the keyboard is up
  open: boolean;
};

function isEditable(element: Element | null) {
  if (!element) return false;
  if (element instanceof HTMLTextAreaElement) return !element.readOnly && !element.disabled;
  if (element instanceof HTMLInputElement) {
    // types that raise no keyboard
    if (/^(button|checkbox|color|file|hidden|image|radio|range|reset|submit)$/.test(element.type)) return false;
    return !element.readOnly && !element.disabled;
  }
  if (element instanceof HTMLSelectElement) return false;
  return element instanceof HTMLElement && element.isContentEditable;
}

// how much of the page the on-screen keyboard covers. ios safari and android chrome (resizes-visual, the default since
// chrome 108) keep the layout viewport when the keyboard opens and only shrink the visual viewport, so anything fixed
// to the bottom ends up behind the keyboard; this reports the covered height so it can move up. 0 on the server,
// during hydration, on desktops, while pinch-zoomed and whenever no text field has focus
export function useKeyboardInset({ disabled = false, threshold = 80, cssVar }: UseKeyboardInsetOptions = {}): KeyboardInset {
  const inset = useMotionValue(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (disabled || typeof window === "undefined") return;
    const viewport = window.visualViewport;
    if (!viewport) return;
    const root = document.documentElement;
    let frame = 0;

    const measure = () => {
      frame = 0;
      const zoomed = viewport.scale > 1.01;
      const covered = isEditable(document.activeElement) && !zoomed ? window.innerHeight - viewport.height - viewport.offsetTop : 0;
      const next = covered > threshold ? Math.round(covered) : 0;
      if (inset.get() !== next) inset.set(next);
      if (cssVar) root.style.setProperty(cssVar, `${next}px`);
      setOpen(next > 0);
    };
    // focus moves before the keyboard animates; the viewport events follow as it slides
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };

    viewport.addEventListener("resize", schedule);
    viewport.addEventListener("scroll", schedule);
    document.addEventListener("focusin", schedule);
    document.addEventListener("focusout", schedule);
    measure();

    return () => {
      cancelAnimationFrame(frame);
      viewport.removeEventListener("resize", schedule);
      viewport.removeEventListener("scroll", schedule);
      document.removeEventListener("focusin", schedule);
      document.removeEventListener("focusout", schedule);
      if (cssVar) root.style.removeProperty(cssVar);
      inset.set(0);
      setOpen(false);
    };
  }, [disabled, threshold, cssVar, inset]);

  return { inset, open };
}
